import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Tabs,
  Tab
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Shield as ShieldIcon,
  CheckCircle as CheckCircleIcon,
  Lock as LockIcon,
  VerifiedUser as VerifiedUserIcon,
  Security as SecurityIcon,
  Fingerprint as FingerprintIcon,
  Speed as SpeedIcon,
  CloudDone as CloudDoneIcon,
  Verified as VerifiedIcon,
  Accessibility as AccessibilityIcon,
  TrendingUp as TrendingUpIcon,
  LockOpen as LockOpenIcon
} from '@mui/icons-material';

const ZeroTrustSecurity: React.FC = () => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = React.useState(1);
  
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    const urls = [
      '/capabilities/high-performance',
      '/capabilities/zero-trust-security',
      '/capabilities/fedramp-ready',
      '/capabilities/section-508-compliant',
      '/capabilities/scalable-architecture',
      '/capabilities/no-vendor-lock-in'
    ];
    navigate(urls[newValue]);
  };

  const benefits = [
    'Multi-factor authentication (MFA) with PIV/CAC card support',
    'Continuous verification of user identity and device health',
    'Role-based access control (RBAC) with granular permissions',
    'End-to-end encryption for data in transit and at rest',
    'Real-time threat detection and automated response',
    'Comprehensive audit logging for compliance and forensics'
  ];

  const capabilities = [
    {
      icon: <LockIcon sx={{ color: '#B31B1B' }} />,
      title: 'Multi-Factor Authentication',
      description: 'Support for PIV/CAC cards, FIDO2 tokens, and biometric authentication'
    },
    {
      icon: <VerifiedUserIcon sx={{ color: '#B31B1B' }} />,
      title: 'Identity Verification',
      description: 'Continuous authentication with adaptive risk-based access controls'
    },
    {
      icon: <SecurityIcon sx={{ color: '#B31B1B' }} />,
      title: 'Threat Detection',
      description: 'AI-powered anomaly detection identifies suspicious activities in real-time'
    },
    {
      icon: <FingerprintIcon sx={{ color: '#B31B1B' }} />,
      title: 'Device Trust',
      description: 'Verify device compliance and health before granting access to resources'
    }
  ];

  const useCases = [
    {
      title: 'Remote Workforce Security',
      description: 'Secure access for remote employees across any device or location with continuous verification'
    },
    {
      title: 'Privileged Access Management',
      description: 'Protect sensitive data with just-in-time access and elevated privilege controls'
    },
    {
      title: 'Compliance Requirements',
      description: 'Meet NIST 800-171, FISMA, and other federal security standards with zero trust architecture'
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{ mb: 3, color: '#003366' }}
      >
        Back to About Page
      </Button>

      {/* Capability Navigation Tabs */}
      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        aria-label="Core Capability tabs"
        variant="scrollable"
        scrollButtons="auto"
        sx={{ 
          mb: 4,
          '.MuiTab-root': {
            color: '#003366',
            minHeight: 64,
            '&.Mui-selected': {
              color: '#003366',
              fontWeight: 700
            }
          },
          '.MuiTabs-indicator': {
            backgroundColor: '#B31B1B',
            height: 3
          }
        }}
      >
        <Tab 
          label="High Performance" 
          icon={<SpeedIcon />} 
          iconPosition="start"
        />
        <Tab 
          label="Zero Trust" 
          icon={<ShieldIcon />} 
          iconPosition="start"
        />
        <Tab 
          label="FedRAMP Ready" 
          icon={<CloudDoneIcon />} 
          iconPosition="start"
        />
        <Tab 
          label="508 Compliant" 
          icon={<AccessibilityIcon />} 
          iconPosition="start"
        />
        <Tab 
          label="Scalable" 
          icon={<TrendingUpIcon />} 
          iconPosition="start"
        />
        <Tab 
          label="No Lock-In" 
          icon={<LockOpenIcon />} 
          iconPosition="start"
        />
      </Tabs>

      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <ShieldIcon sx={{ fontSize: 48, color: '#B31B1B', mr: 2 }} />
          <Typography variant="h3" sx={{ color: '#B31B1B', fontWeight: 'bold' }}>
            Zero Trust Security
          </Typography>
        </Box>
        <Typography variant="h6" color="text.secondary">
          Multi-factor authentication, PIV/CAC support, continuous verification
        </Typography>
      </Box>

      <Paper sx={{ p: 4, mb: 4, borderLeft: '4px solid #B31B1B' }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Typography variant="h5" gutterBottom sx={{ color: '#B31B1B', fontWeight: 600 }}>
              Overview
            </Typography>
            <Typography paragraph sx={{ fontSize: '0.95rem' }}>
              E-Unify implements a comprehensive zero trust security model that assumes no user, device, or network is inherently trustworthy. Every access request is verified, authenticated, and authorized before granting access to resources, ensuring maximum protection for sensitive government data.
            </Typography>
            <Typography sx={{ fontSize: '0.95rem' }}>
              Our zero trust architecture goes beyond traditional perimeter-based security by implementing continuous verification, least-privilege access, and micro-segmentation. This approach significantly reduces the attack surface and limits the potential impact of security breaches.
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="h5" gutterBottom sx={{ color: '#B31B1B', fontWeight: 600 }}>
              Key Benefits
            </Typography>
            <List dense>
              {benefits.map((benefit, index) => (
                <ListItem key={index} sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <CheckCircleIcon sx={{ color: '#B31B1B', fontSize: 20 }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={benefit}
                    primaryTypographyProps={{ fontSize: '0.95rem' }}
                  />
                </ListItem>
              ))}
            </List>
          </Grid>
        </Grid>
      </Paper>

      <Typography variant="h5" gutterBottom sx={{ color: '#B31B1B', fontWeight: 600, mb: 3 }}>
        Security Capabilities
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {capabilities.map((capability, index) => (
          <Grid item xs={12} md={6} key={index}>
            <Card sx={{ height: '100%', borderLeft: '4px solid #B31B1B' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {capability.icon}
                  <Typography variant="h6" sx={{ ml: 1, fontWeight: 600 }}>
                    {capability.title}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {capability.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Typography variant="h5" gutterBottom sx={{ color: '#B31B1B', fontWeight: 600, mb: 3 }}>
        Common Use Cases
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {useCases.map((useCase, index) => (
          <Grid item xs={12} key={index}>
            <Paper sx={{ p: 3, borderLeft: '4px solid #B31B1B' }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                {useCase.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {useCase.description}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Paper 
        sx={{ 
          p: 4, 
          textAlign: 'center', 
          backgroundColor: '#B31B1B',
          color: 'white'
        }}
      >
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          Secure Your Data with Zero Trust
        </Typography>
        <Typography variant="body1" paragraph>
          Learn how E-Unify's zero trust security protects your sensitive information
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={() => navigate('/contact')}
          sx={{
            bgcolor: 'white',
            color: '#B31B1B',
            '&:hover': {
              bgcolor: '#f0f0f0'
            }
          }}
        >
          Contact Us
        </Button>
      </Paper>
    </Container>
  );
};

export default ZeroTrustSecurity;
